import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Search, CheckCircle, XCircle, Users, Plus } from "lucide-react";
import { fetchPartners, updatePartner } from "../actions/partners";
import { fetchDiscountCodes } from "../actions/discountCodes";
import { fetchCustomersName, fetchCustomer } from "../actions/customers";
import LoadingProgress from "../components/LoadingProgress";
import PartnerDiscountManager from "../components/PartnerDiscountManager";
import PartnerForm from "../forms/PartnerForm";
import FeatureBlockedScreen from "../components/FeatureBlockedScreen";
import "../styles/PartnersView.css";

class PartnersView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: "",
      discountModalOpen: false,
      selectedPartner: null,
      partnerModalOpen: false,
      editingPartner: null,
    };

    this.handleActiveToggle = this.handleActiveToggle.bind(this);
    this.handleEditDiscounts = this.handleEditDiscounts.bind(this);
    this.closeDiscountModal = this.closeDiscountModal.bind(this);
    this.handleNewPartner = this.handleNewPartner.bind(this);
    this.handleEditPartner = this.handleEditPartner.bind(this);
    this.closePartnerModal = this.closePartnerModal.bind(this);
    this.handlePartnerSubmit = this.handlePartnerSubmit.bind(this);
  }

  handleActiveToggle(partner, e) {
    e.preventDefault();
    e.stopPropagation();
    const { updatePartner } = this.props;
    const newActiveValue = !partner.isActive;
    updatePartner(partner._id, { isActive: newActiveValue });
  }

  handleEditDiscounts(partner, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      discountModalOpen: true,
      selectedPartner: partner,
    });
  }

  closeDiscountModal() {
    this.setState({
      discountModalOpen: false,
      selectedPartner: null,
    });
  }

  handleNewPartner() {
    this.setState({
      partnerModalOpen: true,
      editingPartner: null,
    });
  }

  handleEditPartner(partner, e) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({
      partnerModalOpen: true,
      editingPartner: partner,
    });
  }

  closePartnerModal() {
    this.setState({
      partnerModalOpen: false,
      editingPartner: null,
    });
  }

  handlePartnerSubmit() {
    this.closePartnerModal();
    // The form submission will trigger a refresh via the Redux state
  }

  componentDidMount() {
    this.props.fetchPartners();
    this.props.fetchDiscountCodes();
    if (this.props.role === 'ADMIN') {
      this.props.fetchCustomersName();
    }
    if (this.props.role === 'CUSTOMER' && this.props.auth && this.props.auth.id) {
      this.props.fetchCustomer(this.props.auth.id);
    }
  }

  componentDidUpdate(prevProps) {
    const { partnersNeedRefresh, discountCodesNeedRefresh } = this.props;

    if (partnersNeedRefresh && !prevProps.partnersNeedRefresh) {
      this.props.fetchPartners();
    }

    if (discountCodesNeedRefresh && !prevProps.discountCodesNeedRefresh) {
      this.props.fetchDiscountCodes();
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  getFilteredPartners() {
    const { partners } = this.props;
    const { searchTerm } = this.state;

    if (!partners) {
      return [];
    }

    let filtered = partners;

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (partner) =>
          partner.name && partner.name.toLowerCase().includes(search),
      );
    }

    // Sort by creation date (newest first)
    return filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }

  getActiveDiscountCodesForPartner(partnerId) {
    const { discountCodes } = this.props;
    if (!discountCodes) return [];

    return discountCodes.filter(code => {
      const assignedPartnerId = code.assigned_partner_id?._id || code.assigned_partner_id;
      return assignedPartnerId === partnerId &&
        code.isActive &&
        (!code.expiresAt || new Date(code.expiresAt) > new Date());
    });
  }

  render() {
    const { loading, error, role, type, customer } = this.props;

    if (role === 'CUSTOMER' && customer && customer.FeaturePartners === false) {
      return (
        <FeatureBlockedScreen featureName="FeaturePartners" featureLabel="Partners" />
      );
    }

    // Only admins can access this view
    if (type === "LICENCIA_BASICA") {
      return (
        <div className="partners-view">
          <div className="container mt-4">
            <div className="alert alert-danger">
              No tienes permisos para acceder a esta sección.
            </div>
          </div>
        </div>
      );
    }

    if (loading || !this.props.partners) {
      return <LoadingProgress />;
    }

    if (error) {
      return (
        <div className="partners-view">
          <div className="container mt-4">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {typeof error === 'object' && error !== null ? error.message || 'Error desconocido' : error}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                this.props.fetchPartners();
              }}
            >
              <i className="fas fa-sync mr-2"></i>
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    const filteredPartners = this.getFilteredPartners();

    return (
      <div className="partners-view">
        <div className=" mt-4">
          <div className="partners-header">
            <div className="header-content">
              <div className="header-text">
                <h1>
                  <i className="fas fa-users mr-3"></i>
                  Partners
                </h1>
                <span className="subtitle-inline">
                  Gestiona los partners y proveedores del sistema
                </span>
              </div>
              <div className="search-input-wrapper">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por nombre..."
                  value={this.state.searchTerm}
                  onChange={this.handleSearchChange}
                />
              </div>
              <div className="header-actions">
                <button
                  className="btn btn-primary"
                  onClick={this.handleNewPartner}
                >
                  <Plus size={16} className="mr-2" />
                  Nuevo Partner
                </button>
              </div>
            </div>
          </div>

          <div className="templates-container">
            <div className="templates-info">
              {filteredPartners.length} partners registrados
            </div>

            <div className="templates-grid">
              {filteredPartners.map((partner) => (
                <div
                  key={partner._id}
                  className="template-card"
                  style={{ cursor: 'default' }}
                >
                  <div className="template-card-header">
                    <div className="customer-avatar">
                      {partner.name
                        ? partner.name.charAt(0).toUpperCase()
                        : "?"}
                    </div>
                    <div className="customer-info">
                      <h3 className="customer-name">{partner.name}</h3>
                      <p className="customer-email">{partner.email}</p>
                    </div>
                  </div>

                  <div className="template-card-body">
                    <div className="template-status">
                      <div className="partner-details">
                        <small>
                          <i className="fas fa-phone mr-1"></i>
                          {partner.phone || "Sin teléfono"}
                        </small>
                        <br />
                        <small>
                          <i className="fas fa-map-marker-alt mr-1"></i>
                          {partner.city || "Sin ciudad"}
                        </small>
                        <br />
                        <small>
                          <i className="fas fa-tag mr-1"></i>
                          {partner.partnerType || "OTHER"}
                        </small>
                      </div>
                    </div>

                    {/* Active Discount Codes Section */}
                    <div className="partner-discount-codes" style={{ marginTop: '15px' }}>
                      <small style={{ fontWeight: 'bold', color: '#666' }}>
                        <i className="fas fa-percent mr-1"></i>
                        Códigos de descuento activos:
                      </small>
                      <div style={{ marginTop: '5px' }}>
                        {(() => {
                          const activeCodes = this.getActiveDiscountCodesForPartner(partner._id);
                          if (activeCodes.length === 0) {
                            return (
                              <small style={{ color: '#999', fontStyle: 'italic' }}>
                                Ningún código activo
                              </small>
                            );
                          }
                          return activeCodes.slice(0, 3).map((code, index) => (
                            <div key={code._id} style={{
                              display: 'inline-block',
                              backgroundColor: '#e8f5e8',
                              color: '#28a745',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              fontWeight: 'bold',
                              margin: '2px',
                              border: '1px solid #c3e6c3'
                            }}>
                              {code.code}
                              {code.discountType === 'PERCENTAGE' ? ` ${code.discountValue}%` : ` €${code.discountValue}`}
                            </div>
                          ));
                        })()}
                        {(() => {
                          const activeCodes = this.getActiveDiscountCodesForPartner(partner._id);
                          if (activeCodes.length > 3) {
                            return (
                              <small style={{ color: '#666', fontStyle: 'italic' }}>
                                +{activeCodes.length - 3} más
                              </small>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>

                  <div className="template-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <span className="btn-edit" onClick={(e) => this.handleEditPartner(partner, e)}>
                        <i className="fas fa-edit mr-2"></i>
                        Editar
                      </span>
                      <span className="btn-edit" onClick={(e) => this.handleEditDiscounts(partner, e)}>
                        <i className="fas fa-tags mr-2"></i>
                        Gestionar descuentos
                      </span>
                    </div>

                    <div className="template-active-toggle" onClick={(e) => this.handleActiveToggle(partner, e)}>
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          backgroundColor: partner.isActive ? '#28a745' : '#dc3545',
                          borderRadius: '25px',
                          padding: '4px',
                          cursor: 'pointer',
                          width: '90px',
                          height: '32px',
                          position: 'relative',
                          transition: 'background-color 0.3s ease',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}
                        title={partner.isActive ? 'Click para desactivar' : 'Click para activar'}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            backgroundColor: 'white',
                            borderRadius: '50%',
                            width: '24px',
                            height: '24px',
                            transition: 'transform 0.3s ease',
                            transform: partner.isActive ? 'translateX(60px)' : 'translateX(2px)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                          }}
                        />
                        <span
                          style={{
                            color: 'white',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            position: 'absolute',
                            left: partner.isActive ? '8px' : 'auto',
                            right: partner.isActive ? 'auto' : '8px',
                            userSelect: 'none',
                          }}
                        >
                          {partner.isActive ? 'ACTIVO' : 'INACTIVO'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredPartners.length === 0 && (
              <div className="no-results">
                <i className="fas fa-search fa-3x mb-3"></i>
                <h3>No se encontraron partners</h3>
                <p>Intenta con otro término de búsqueda</p>
              </div>
            )}
          </div>
        </div>

        <PartnerDiscountManager
          isOpen={this.state.discountModalOpen}
          partner={this.state.selectedPartner}
          discountCodes={this.props.discountCodes}
          onClose={this.closeDiscountModal}
        />

        {this.state.partnerModalOpen && (
          <div className="modal-overlay" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1050
          }}>
            <div className="modal-dialog modal-lg" style={{
              margin: 0,
              maxWidth: '800px',
              width: '90%'
            }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {this.state.editingPartner ? 'Editar Partner' : 'Nuevo Partner'}
                  </h5>
                  <button
                    type="button"
                    className="close"
                    onClick={this.closePartnerModal}
                  >
                    <span>&times;</span>
                  </button>
                </div>
                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <PartnerForm
                    editingPartner={this.state.editingPartner}
                    onSubmit={this.handlePartnerSubmit}
                    onCancel={this.closePartnerModal}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  role: state.auth.role,
  type: state.auth.type,
  customer: state.customers.customer,
  partners: state.partners.partners,
  partnersNeedRefresh: state.partners.partnersNeedRefresh,
  discountCodes: state.discountCodes.discountCodes,
  discountCodesNeedRefresh: state.discountCodes.discountCodesNeedRefresh,
  customers: state.customers.customers,
  loading: state.partners.loading || state.discountCodes.loading,
  error: state.partners.error || state.discountCodes.error,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchPartners,
      updatePartner,
      fetchDiscountCodes,
      fetchCustomersName,
      fetchCustomer,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(PartnersView);